import { useState, useEffect, useRef, lazy } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowRight, Clock, Truck, ChevronRight, Pill, Heart, Activity, Star, CheckCircle, X, DollarSign, Scale, HelpCircle, Gift, Smartphone, Copy, Sparkles, Flame, Menu, Play, ShieldCheck, Footprints, Bell, Utensils, Dumbbell, BarChart3, Bot, Lock } from "lucide-react";
import InstallAppInline from "@/components/InstallAppInline";
import LandingRewardsPerkTile from "@/components/LandingRewardsPerkTile";
import LandingStateRequestBanner from "@/components/LandingStateRequestBanner";
import LandingComparePlansStrip from "@/components/LandingComparePlansStrip";
import LandingFaqList from "@/components/LandingFaqList";
import PharmacyLink from "@/components/PharmacyLink";
import StickyComparePlansFab from "@/components/StickyComparePlansFab";
import ButterflyEasterEgg from "@/components/ButterflyEasterEgg";
import FitQuizModal from "@/components/FitQuizModal";
import GeoShippingTeaser from "@/components/GeoShippingTeaser";
import LiveStepCounter from "@/components/LiveStepCounter";
import MillionMileTicker from "@/components/MillionMileTicker";
import { HIPAABadge } from "@/components/HIPAACompliance";
import LandingFeaturedDealsStrip from "@/components/LandingFeaturedDealsStrip";
import FreeShippingTruckBanner from "@/components/FreeShippingTruckBanner";
import LanguageToggle from "@/components/LanguageToggle";
import { Button } from "@/components/ui/button";
import API from "@/lib/api";
import { trackSignupClick } from "@/lib/track";
import { toast } from "sonner";
import SectionCTA, { FxSparks, FX_WARM, FX_COOL } from "@/components/landing/SectionCTA";
import SingleFoot from "@/components/landing/Footprints";
import RollingBanner from "@/components/landing/RollingBanner";
import ServingStatesStrip from "@/components/ServingStatesStrip";
import LazyOnVisible from "@/components/LazyOnVisible";
import { fadeUp } from "@/components/landing/motion";
import StarterDosePopup from "@/components/landing/StarterDosePopup";
import useWellnessFeatures from "@/hooks/useWellnessFeatures";

// Below-fold landing sections — code-split so the hero + rolling banner
// paint before we fetch these chunks. Each still eagerly loads at scroll.
const WeightLossCalculator = lazy(() => import("@/components/landing/WeightLossCalculator"));
const GLP1ProgressGraph    = lazy(() => import("@/components/landing/GLP1ProgressGraph"));
const FactOrMythCarousel   = lazy(() => import("@/components/landing/FactOrMythCarousel"));
const LandingYouTubeCarousel = lazy(() => import("@/components/landing/LandingYouTubeCarousel"));

const HERO_IMG = "https://images.unsplash.com/photo-1713865469952-ab0c56bbddd7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxoYXBweSUyMHBhdGllbnQlMjB0ZWxlaGVhbHRoJTIwYnJpZ2h0fGVufDB8fHx8MTc3NjExNTk5NXww&ixlib=rb-4.1.0&q=85&w=800";
const WELLNESS_IMG = "https://images.unsplash.com/photo-1772450427624-455b0411fa92?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxoZWFsdGh5JTIwYWN0aXZlJTIwbGlmZXN0eWxlfGVufDB8fHx8MTc3NjExNTk5NXww&ixlib=rb-4.1.0&q=85&w=800";

export default function LandingPage() {
  const navigate = useNavigate();
  const wellness = useWellnessFeatures();
  const stepathonComingSoon = !wellness.step_counter_enabled;
  const companionComingSoon = !wellness.companion_app_enabled;
  const [banners, setBanners] = useState([]);
  const [referOpen, setReferOpen] = useState(false);
  const [fitQuizOpen, setFitQuizOpen] = useState(false);
  // Deep-link support: a URL like /?quiz=1, /#fit-quiz, or the clean
  // path /quiz all auto-open the Fit Quiz modal. `/quiz` is the QR-
  // friendly form — no query params, no ampersands that some QR
  // generators mangle or drop.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const hash = (window.location.hash || "").toLowerCase();
      const path = (window.location.pathname || "").toLowerCase();
      if (
        params.get("quiz") === "1" ||
        hash === "#fit-quiz" ||
        path === "/quiz" || path === "/quiz/"
      ) {
        setFitQuizOpen(true);
      }
    } catch { /* SSR safety */ }
  }, []);
  const [pharmacyName, setPharmacyName] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // FAQ teaser starts collapsed — visitor must tap "Click here to expand" to
  // reveal the question list. Keeps the landing page short on first scroll.
  const [faqExpanded, setFaqExpanded] = useState(false);

  // Lock body scroll + close on ESC whenever the mobile nav sheet is open.
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setMobileMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [mobileMenuOpen]);
  // Live-pull referral reward amount from the public loyalty-rules endpoint
  // so the landing copy auto-updates when admin tweaks it in
  // Admin → Loyalty & Coupons → Rules. Falls back to $20 if the endpoint
  // is unreachable (matches current configured default).
  const [referralReward, setReferralReward] = useState(20);
  // WCAG 2.1 — when the OS reports motion sensitivity, suppress all
  // decorative motion (molecular dots, hero icons, banner sparkles).
  // Static visuals are still shown so the page never feels broken.
  const prefersReducedMotion = useReducedMotion();

  const referralUrl = typeof window !== "undefined" ? `${window.location.origin}/register` : "/register";

  const copyReferralLink = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
      toast.success("Referral link copied!");
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  const shareReferral = async () => {
    // Prefer the native OS share sheet on phones; fall back to copy elsewhere.
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Save $${referralReward} on GLP-1 medications`,
          text: `Join me on GLPFlo — licensed telehealth for GLP-1s. We both get $${referralReward} off.`,
          url: referralUrl,
        });
        return;
      } catch { /* user cancelled */ }
    }
    copyReferralLink();
  };

  useEffect(() => {
    API.get("/banners").then(({ data }) => {
      // Only keep rolling banners for the top ticker. The in-page "Free
      // Consultation" promo popup was removed — the hero already has two
      // signup CTAs plus the fit quiz, so an interstitial is redundant.
      setBanners((data || []).filter((b) => b.type !== "popup"));
    }).catch(() => {});
  }, []);

  // Pull the live LabFlo pharmacy partner name for the hero trust line.
  useEffect(() => {
    API.get("/pharmacy-tenants")
      .then(({ data }) => {
        const first = (data || []).find(p => p && p.status === "active" && p.name);
        if (first) setPharmacyName(first.name);
      })
      .catch(() => {});
  }, []);

  // Live-pull referral reward amount (public endpoint, no auth needed).
  useEffect(() => {
    API.get("/loyalty-rules/public")
      .then(({ data }) => {
        const amt = Number(data?.referral_amount);
        if (Number.isFinite(amt) && amt > 0) setReferralReward(Math.round(amt));
      })
      .catch(() => { /* keep fallbacks */ });
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="landing-page">
      {/* State-request banner — announces our Florida-only footprint and
          collects waitlist signups from other states. Submissions land
          in `state_waitlist` and appear on the admin dashboard. */}
      <LandingStateRequestBanner />
      <LandingComparePlansStrip />
      {/* Rolling Banner */}
      <RollingBanner banners={banners} />
      <ServingStatesStrip />

      {/* Million Mile Movement live ticker — visible to every visitor,
          even before login. Pulses + animates the umbrella counter and
          links straight to /stepathon. */}
      <MillionMileTicker />

      <StickyComparePlansFab />
      <ButterflyEasterEgg />
      <FitQuizModal open={fitQuizOpen} onOpenChange={setFitQuizOpen} />
      <StarterDosePopup onQualify={() => setFitQuizOpen(true)} />

      {/* Refer-a-Friend Share Modal — intentionally client-side only. Avoids
          routing to /register, which would bounce an authenticated staff user
          (admin/prescriber) to their dashboard via PublicRoute. */}
      <AnimatePresence>
        {referOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/10 p-4" onClick={() => setReferOpen(false)} data-testid="refer-modal">
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} onClick={(ev) => ev.stopPropagation()} className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
              <button onClick={() => setReferOpen(false)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors" data-testid="refer-modal-close"><X className="w-4 h-4" /></button>
              <div className="w-12 h-12 rounded-2xl bg-purple-100 flex items-center justify-center mb-4"><Gift className="w-6 h-6 text-purple-600" /></div>
              <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Refer a friend, get ${referralReward}</h3>
              <p className="text-sm text-slate-500 mb-5">Share this link. When they complete their first order, you both get ${referralReward} off.</p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
                <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Your share link</p>
                <p className="font-mono text-xs text-slate-900 break-all select-all" data-testid="refer-link-text">{referralUrl}</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={copyReferralLink} variant="outline" className="flex-1 rounded-full" data-testid="refer-copy-btn">
                  <Copy className="w-4 h-4 mr-1" /> Copy link
                </Button>
                <Button onClick={shareReferral} className="flex-1 rounded-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="refer-share-btn">
                  <ArrowRight className="w-4 h-4 mr-1" /> Share
                </Button>
              </div>
              <p className="text-xs text-slate-400 text-center mt-4">Already a patient? Log in to track your referrals in your profile.</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white border-b border-slate-100" data-testid="landing-nav">
        <div className="relative max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-between h-14 sm:h-16 gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center"><Pill className="w-4 h-4 text-white" /></div>
            <span className="font-bold text-xl tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>GLP<span className="text-purple-600">Flo</span></span>
          </Link>
          {/* Centered nav links — only desktop (lg+). Tablet & below get the
              compact "Pricing + Sign In + Get Started" trio on the right so
              nothing overlaps on Samsung tablets / iPad portrait widths. */}
          <div className="hidden lg:flex items-center gap-8 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <a href="#services" className="text-sm text-slate-600 hover:text-purple-600 transition-colors" data-testid="nav-services">Services</a>
            <a href="#how-it-works" className="text-sm text-slate-600 hover:text-purple-600 transition-colors" data-testid="nav-how-it-works">The Process</a>
            <a href="#medications" className="text-sm text-slate-600 hover:text-purple-600 transition-colors" data-testid="nav-medications">Medications</a>
            <Link to="/pricing" className="text-sm text-slate-600 hover:text-purple-600 transition-colors" data-testid="nav-pricing">Pricing</Link>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {/* Pricing link visible on tablet (md-lg) where the centered nav is hidden */}
            <Link to="/pricing" className="hidden md:inline lg:hidden text-sm text-slate-600 hover:text-purple-600 transition-colors" data-testid="nav-pricing-tablet">Pricing</Link>
            <Button variant="ghost" className="rounded-full text-slate-600 hidden sm:flex" onClick={() => navigate("/login")} data-testid="nav-login-btn">Sign In</Button>
            <Button
              className="rounded-full text-purple-950 font-extrabold px-4 sm:px-6 text-sm sm:text-base shadow-lg shadow-yellow-400/50 ring-2 ring-yellow-300 animate-pulse hover:animate-none hover:scale-105 transition-transform"
              style={{
                background: "linear-gradient(135deg,#fde047,#facc15,#eab308)",
                animationDuration: "1.5s",
              }}
              onClick={() => { trackSignupClick("nav-get-started"); navigate("/register"); }}
              data-testid="nav-get-started-btn"
            >
              Sign Up Now <ArrowRight className="w-4 h-4 ml-1 hidden sm:inline" />
            </Button>
            {/* Hamburger — only on tablet/phone (hidden once the centered nav is back at lg+) */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full text-slate-600 hover:text-purple-600 hover:bg-purple-50 transition-colors -mr-1"
              aria-label="Open navigation menu"
              data-testid="nav-hamburger-btn"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </nav>

      {/* Animated Free Shipping truck banner — drives across the strip so
          patients see shipping is included in the flat price before they
          even scroll to the hero. Live on landing + all /pricing pages. */}
      <FreeShippingTruckBanner />

      {/* Mobile / tablet navigation overlay sheet — anchor links for Services /
          How It Works / Medications / Pricing / FAQ. Hidden at lg+ where the
          centered nav already exposes these. Backdrop click + Escape close. */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="fixed inset-0 z-[100] lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            data-testid="mobile-nav-overlay"
          >
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setMobileMenuOpen(false)}
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
              data-testid="mobile-nav-backdrop"
            />
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 280, damping: 30 }}
              className="absolute top-0 right-0 h-full w-[82%] max-w-[320px] bg-white shadow-2xl flex flex-col"
              data-testid="mobile-nav-sheet"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center"><Pill className="w-4 h-4 text-white" /></div>
                  <span className="font-bold text-lg tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>GLP<span className="text-purple-600">Flo</span></span>
                </Link>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                  aria-label="Close menu"
                  data-testid="mobile-nav-close-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <nav className="flex flex-col px-3 py-4 gap-1">
                {/* Personalised geo-IP shipping pre-flight check — shows
                    instantly at the top of the menu, turns the hamburger
                    into a personalised pre-flight check. */}
                <GeoShippingTeaser />
                {[
                  { label: "Services", href: "#services", icon: Activity, testid: "mobile-nav-services" },
                  { label: "The Process", href: "#how-it-works", icon: Clock, testid: "mobile-nav-how-it-works" },
                  { label: "Medications", href: "#medications", icon: Pill, testid: "mobile-nav-medications" },
                ].map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 hover:bg-purple-50 hover:text-purple-700 transition-colors text-base font-medium"
                    data-testid={item.testid}
                  >
                    <item.icon className="w-5 h-5 text-purple-500" />
                    {item.label}
                  </a>
                ))}
                <Link
                  to="/pricing"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 hover:bg-purple-50 hover:text-purple-700 transition-colors text-base font-medium"
                  data-testid="mobile-nav-pricing"
                >
                  <DollarSign className="w-5 h-5 text-purple-500" />
                  Pricing
                </Link>
                <Link
                  to="/faq"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 hover:bg-purple-50 hover:text-purple-700 transition-colors text-base font-medium"
                  data-testid="mobile-nav-faq"
                >
                  <HelpCircle className="w-5 h-5 text-purple-500" />
                  FAQ
                </Link>
                {/* Refer-a-friend banner — high-engagement placement inside
                    the hamburger keeps the existing referral modal one tap
                    away. Reward $$ is wired live from the loyalty API. */}
                <button
                  type="button"
                  onClick={() => { setMobileMenuOpen(false); setReferOpen(true); }}
                  className="mt-3 mx-1 px-4 py-3.5 rounded-2xl text-left bg-gradient-to-br from-purple-600 via-purple-700 to-violet-800 text-white shadow-md shadow-purple-200 flex items-center gap-3 group active:scale-[0.98] transition-transform"
                  data-testid="mobile-nav-refer-banner"
                >
                  <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center shrink-0">
                    <Gift className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold flex items-center gap-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      <Star className="w-3.5 h-3.5 text-amber-300 fill-amber-300" />
                      Refer a friend, get ${referralReward}
                    </p>
                    <p className="text-[11px] text-purple-100">They get ${referralReward} off too · tap to share</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-white/80 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </nav>
              <div className="mt-auto px-5 pb-6 pt-3 border-t border-slate-100 space-y-2">
                {/* PWA install nudge — iOS / Android only; auto-hides when
                    already running standalone. Surfaced here because users
                    poking at the hamburger menu are exactly the cohort most
                    likely to bookmark / pin / install. */}
                <div className="mb-3" data-testid="mobile-nav-pwa-install">
                  <InstallAppInline compact />
                </div>
                <Button
                  variant="outline"
                  className="w-full rounded-full border-slate-200 text-slate-700 hover:bg-slate-50"
                  onClick={() => { setMobileMenuOpen(false); navigate("/login"); }}
                  data-testid="mobile-nav-signin-btn"
                >
                  Sign In
                </Button>
                <Button
                  className="w-full rounded-full bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={() => { trackSignupClick("mobile-nav-get-started"); setMobileMenuOpen(false); navigate("/register"); }}
                  data-testid="mobile-nav-get-started-btn"
                >
                  Sign Up Now <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-white to-purple-100" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16 sm:pt-16 sm:pb-24 lg:pt-24 lg:pb-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.6 }}>
              {/* Mobile-only stacked new-patient plaques (Sema + Tirz).
                  Desktop versions live on the right column above the hero
                  image, which is hidden below lg. */}
              <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div
                  className="w-full text-white text-center px-4 py-3 shadow-md shadow-fuchsia-900/40 border-y-2 border-amber-300/80"
                  style={{ background: "linear-gradient(120deg, #7e22ce 0%, #c026d3 50%, #db2777 100%)" }}
                  data-testid="hero-starter-dose-badge-sema-mobile"
                >
                  <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-amber-200">Semaglutide · Royal 3-Mo</p>
                  <p className="mt-0.5 text-3xl font-extrabold text-white leading-none" style={{ fontFamily: "'Outfit', sans-serif" }}>$79.99</p>
                  <p className="mt-1 text-[10px] font-medium text-white/85">first month · new patients only</p>
                </div>
                <div
                  className="w-full text-white text-center px-4 py-3 shadow-md shadow-purple-900/40 border-y-2 border-amber-300/80"
                  style={{ background: "linear-gradient(120deg, #4c1d95 0%, #6d28d9 50%, #7c3aed 100%)" }}
                  data-testid="hero-starter-dose-badge-tirz-mobile"
                >
                  <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-amber-200">Tirzepatide · Royal 3-Mo</p>
                  <p className="mt-0.5 text-3xl font-extrabold text-white leading-none" style={{ fontFamily: "'Outfit', sans-serif" }}>$99.99</p>
                  <p className="mt-1 text-[10px] font-medium text-white/85">first month · new patients only</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setFitQuizOpen(true)}
                className="group relative mb-6 mx-auto sm:mx-0 flex sm:inline-flex w-full sm:w-auto sm:min-w-[28rem] items-center justify-center text-center gap-3 rounded-full px-8 sm:px-12 py-4 text-base sm:text-lg font-bold text-white shadow-lg shadow-purple-500/40 transition-all hover:scale-[1.03] hover:shadow-purple-500/60 active:scale-[0.99] focus:outline-none focus:ring-4 focus:ring-purple-300 animate-pulse-glow overflow-hidden"
                style={{
                  background: "linear-gradient(110deg, #9333ea 0%, #7c3aed 35%, #c026d3 65%, #7c3aed 100%)",
                  backgroundSize: "200% 100%",
                  animation: "pulse-glow 2.6s ease-in-out infinite, shimmer 3.5s linear infinite",
                }}
                data-testid="hero-fit-quiz-cta"
              >
                {/* shimmer overlay */}
                <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-full"
                  style={{
                    background: "linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.32) 50%, transparent 70%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 3.5s linear infinite",
                  }} />

                {/* sparkles around the button */}
                <Sparkles aria-hidden="true" className="absolute -top-2 -left-1.5 w-4 h-4 text-fuchsia-200 animate-sparkle-float-slow" />
                <Sparkles aria-hidden="true" className="absolute -bottom-2 right-6 w-3 h-3 text-violet-200 animate-sparkle-float-fast" />
                <Sparkles aria-hidden="true" className="absolute top-1.5 right-2 w-3.5 h-3.5 text-purple-100 animate-sparkle-spin" style={{ animationDelay: "0.4s" }} />
                <Sparkles aria-hidden="true" className="absolute bottom-1.5 left-3 w-2.5 h-2.5 text-pink-200 animate-sparkle-float-slow" style={{ animationDelay: "1.1s" }} />

                <Sparkles className="relative w-5 h-5 sm:w-6 sm:h-6 drop-shadow-md group-hover:rotate-12 transition-transform" />
                <span className="relative flex flex-col items-center leading-tight">
                  <span>Am I a good fit for GLP-1s?</span>
                  <span className="text-xs font-medium text-white/90 mt-0.5" data-testid="hero-fit-quiz-cta-subtext">take the projection test</span>
                </span>
                <ArrowRight className="relative w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform" />
              </button>
              <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold tracking-tight text-black leading-[1.1] mb-4 sm:mb-6 text-center md:text-left" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Weight loss, <span className="text-purple-600">without the friction</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-lg text-center md:text-left mx-auto md:mx-0">
                Access physician-prescribed compounded GLP-1 medications through our licensed telehealth platform. Provider-reviewed, pharmacy-compounded, delivered to your door.
              </p>
              {pharmacyName && (
                <div className="mb-6 inline-flex mx-auto md:mx-0 items-center gap-2 bg-white/80 backdrop-blur-sm border border-purple-200 rounded-full px-3.5 py-1.5 text-xs text-slate-700 shadow-sm" data-testid="hero-pharmacy-partner">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-purple-100 text-purple-700">
                    <Pill className="w-3 h-3" />
                  </span>
                  <span>
                    <span className="text-slate-500">Trusted pharmacy partner:</span>{" "}
                    <span className="font-semibold text-slate-900">{pharmacyName}</span>
                    <span className="hidden sm:inline text-slate-400"> · US-licensed 503A compounding</span>
                  </span>
                </div>
              )}
              {/* HIPAA Compliant trust badge — reassures visitors that PHI
                  is protected before they hand over any health info. */}
              <div className="mb-4 flex flex-wrap items-center gap-2 justify-center md:justify-start">
                <HIPAABadge variant="pill" data-testid="hero-hipaa-badge" />
                <span
                  data-testid="hero-us-clinical-care-badge"
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-wider bg-gradient-to-r from-emerald-100 via-white to-emerald-100 border border-emerald-300 text-emerald-800 shadow-sm"
                >
                  100% US-based clinical care team
                </span>
                <span className="text-[11px] text-slate-500 text-center md:text-left">
                  Bank-grade encryption · Access-logged · Never sold to advertisers
                </span>
              </div>

              {/* Rotating patient testimonial removed for LegitScript
                  compliance — no unverified testimonials on the public
                  site. */}

              {/* Flat-price inclusions — free shipping + physician consult
                  are the two friction-removers patients ask about most on
                  the hero, per landing analytics. */}
              <div className="mb-5 flex flex-wrap items-center gap-2 justify-center md:justify-start" data-testid="hero-included-in-price">
                <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-bold bg-emerald-500/15 border border-emerald-400/40 text-emerald-700 shadow-sm">
                  <Truck className="w-3.5 h-3.5" /> Free cold-chain shipping
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-bold bg-purple-500/15 border border-purple-400/40 text-purple-700 shadow-sm">
                  <Heart className="w-3.5 h-3.5" /> Physician consult included
                </span>
                <span className="text-[11px] text-slate-500">— all inside the flat monthly price. No hidden fees.</span>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center md:items-start">
                <div className="relative inline-block" data-testid="hero-get-started-fw">
                  <FxSparks colors={FX_WARM} />
                  <Button className="fw-flash animate-pulse relative z-[2] rounded-full bg-purple-600 hover:bg-purple-700 text-white px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg font-bold shadow-[0_0_40px_-10px_rgba(147,51,234,0.3)]" onClick={() => { trackSignupClick("hero-get-started"); navigate("/register"); }} data-testid="hero-get-started-btn">
                    Start the Journey / Sign Up <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
                <div className="relative inline-block" data-testid="hero-see-pricing-fw">
                  <FxSparks colors={FX_COOL} />
                  <Button
                    className="fw-flash animate-pulse relative z-[2] rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg font-bold shadow-lg shadow-emerald-500/40"
                    onClick={() => navigate("/pricing")}
                    data-testid="hero-see-pricing-btn"
                  >
                    See Full Pricing <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
              <div className="flex items-center justify-center md:justify-start gap-4 sm:gap-6 mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-purple-100">
                <div className="text-center"><p className="text-2xl font-bold text-slate-900">70k+</p><p className="text-xs text-slate-500">Patients Treated Through Polomar Pharmacy</p></div>
                <div className="w-px h-8 bg-purple-100" />
                <div className="text-center"><p className="text-2xl font-bold text-slate-900">98%</p><p className="text-xs text-slate-500">Satisfaction</p></div>
                <div className="w-px h-8 bg-purple-100" />
                <div className="text-center"><p className="text-2xl font-bold text-slate-900">24hr</p><p className="text-xs text-slate-500">Avg Review</p></div>
              </div>
              {/* Referral Badge — opens a share modal. Intentionally NOT a
                  router navigation. A logged-in admin/prescriber hitting
                  `/register` would be bounced into their staff dashboard by
                  PublicRoute, which looked like an auto-admin-login. Keeping
                  this as a client-side share action removes that risk. */}
              <button onClick={() => setReferOpen(true)} className="mt-3 w-full bg-gradient-to-r from-purple-600 to-violet-700 hover:from-purple-700 hover:to-violet-800 border border-purple-700 rounded-xl px-4 py-3 flex items-center gap-3 text-left transition-all hover:shadow-lg hover:shadow-purple-200 group" data-testid="referral-landing-badge">
                <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                  <Gift className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white">Refer a friend, get ${referralReward}</p>
                  <p className="text-purple-100 text-xs">They get a ${referralReward} off coupon too.</p>
                </div>
                <ArrowRight className="w-4 h-4 text-white group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="hidden lg:block lg:self-start">
              {/* Flashy $79.99 starter-dose plaque — bright fuchsia
                  gradient, oversized price, pulsing glow. Sits above
                  the Florida plaque so the offer is the first thing
                  the eye lands on. */}
              <div
                className="mb-4 w-full text-white text-center px-6 py-5 shadow-xl shadow-fuchsia-900/40 border-y-2 border-amber-300/80 relative overflow-hidden"
                style={{
                  background:
                    "linear-gradient(120deg, #7e22ce 0%, #c026d3 50%, #db2777 100%)",
                }}
                data-testid="hero-starter-dose-badge"
              >
                <div className="flex items-center justify-center gap-2 text-amber-200">
                  <Sparkles className="w-4 h-4" aria-hidden="true" />
                  <span className="text-[11px] font-bold uppercase tracking-[0.4em]">Limited-Time Intro</span>
                  <Sparkles className="w-4 h-4" aria-hidden="true" />
                </div>
                <p className="mt-1 text-[13px] font-semibold text-white/90 uppercase tracking-[0.15em]">Semaglutide · First Month of Royal 3-Mo Tier</p>
                {/* Inline-block wrapper so the pulsing boxShadow hugs the
                    price text only — otherwise a block-level <p> would let
                    the flash span the full width of the plaque. */}
                <div className="mt-1 flex justify-center">
                  <span
                    className="inline-block text-6xl xl:text-7xl font-extrabold text-white leading-none rounded-md px-1"
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      textShadow:
                        "0 0 12px rgba(255,255,255,0.55), 0 0 28px rgba(252,211,77,0.7)",
                      animation: "pulse-glow 2.4s ease-in-out infinite",
                    }}
                  >
                    $79.99
                  </span>
                </div>
                <p className="mt-1 text-[11px] font-medium text-white/85">for your first month · new GLPFlo patients only</p>
              </div>
              {/* Tirzepatide new-patient plaque — mirrors the Sema plaque
                  above so both drugs get equal hero real estate. Same
                  gradient, sparkles, and pulsing price treatment. */}
              <div
                className="mb-6 w-full text-white text-center px-6 py-5 shadow-xl shadow-purple-900/40 border-y-2 border-amber-300/80 relative overflow-hidden"
                style={{
                  background:
                    "linear-gradient(120deg, #4c1d95 0%, #6d28d9 50%, #7c3aed 100%)",
                }}
                data-testid="hero-starter-dose-badge-tirz"
              >
                <div className="flex items-center justify-center gap-2 text-amber-200">
                  <Sparkles className="w-4 h-4" aria-hidden="true" />
                  <span className="text-[11px] font-bold uppercase tracking-[0.4em]">Limited-Time Intro</span>
                  <Sparkles className="w-4 h-4" aria-hidden="true" />
                </div>
                <p className="mt-1 text-[13px] font-semibold text-white/90 uppercase tracking-[0.15em]">Tirzepatide · First Month of Royal 3-Mo Tier</p>
                <div className="mt-1 flex justify-center">
                  <span
                    className="inline-block text-6xl xl:text-7xl font-extrabold text-white leading-none rounded-md px-1"
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      textShadow:
                        "0 0 12px rgba(255,255,255,0.55), 0 0 28px rgba(252,211,77,0.7)",
                      animation: "pulse-glow 2.4s ease-in-out infinite",
                    }}
                  >
                    $99.99
                  </span>
                </div>
                <p className="mt-1 text-[11px] font-medium text-white/85">for your first month · new GLPFlo patients only</p>
              </div>
              <div className="relative">
                <div className="absolute -inset-4 bg-purple-200/30 rounded-3xl blur-2xl" />
                <img src={HERO_IMG} alt="Patient using telehealth" className="relative rounded-3xl shadow-2xl w-full h-[480px] object-cover" />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-lg p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-emerald-600" /></div>
                  <div><p className="text-sm font-semibold text-slate-900">Licensed Providers</p><p className="text-xs text-slate-500">Board-certified physicians</p></div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* US-based clinical care callout — bold ribbon reassuring visitors
          that every prescription is written and every message answered by
          a US-licensed clinician (parity with the mobile app footer). */}
      <section className="bg-gradient-to-r from-emerald-50 via-white to-emerald-50 border-y border-emerald-100 py-6" data-testid="us-clinical-care-ribbon">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center gap-4 md:gap-6 text-center md:text-left">
          <div className="relative flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 flex-shrink-0" aria-label="United States">
            <ShieldCheck className="w-7 h-7 text-emerald-700" />
            <span className="absolute inset-0 rounded-full ring-2 ring-emerald-400 opacity-60 animate-ping" />
          </div>
          <div className="flex-1">
            <p className="text-lg sm:text-xl font-extrabold text-emerald-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              100% US-based clinical care team
            </p>
            <p className="text-sm text-emerald-800/80 mt-0.5">
              Every prescription written and every message answered by a US-licensed clinician — never outsourced offshore. Board-certified providers, HIPAA-compliant, and reachable seven days a week.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-emerald-800">
            <span className="px-3 py-1 rounded-full bg-white border border-emerald-200">US-licensed</span>
            <span className="px-3 py-1 rounded-full bg-white border border-emerald-200">Board-certified</span>
            <span className="px-3 py-1 rounded-full bg-white border border-emerald-200">HIPAA-compliant</span>
          </div>
        </div>
      </section>

      {/* Featured marketing deals — hydrated from active coupon codes
          with hero photos. Auto-hides when the catalog is empty. */}
      <LandingFeaturedDealsStrip />

      {/* Services Bento */}
      <section id="services" className="py-12 lg:py-16 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* No-insurance reassurance ribbon — punchy, on-brand purple→pink
              gradient. Sits above the "Treatments Tailored to You" header so
              cost-conscious visitors immediately see the headline benefit
              before scrolling into the medication cards. */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.4 }}
            className="mb-12 flex justify-center"
            data-testid="no-insurance-banner"
          >
            <div className="relative inline-flex items-center gap-5 sm:gap-7 rounded-full bg-gradient-to-r from-purple-600 via-fuchsia-600 to-pink-600 px-10 sm:px-14 py-6 sm:py-8 shadow-2xl shadow-purple-300/50 shimmer-pill">
              {/* Subtle outer glow */}
              <span aria-hidden className="absolute -inset-1 rounded-full bg-gradient-to-r from-purple-500/40 via-fuchsia-500/40 to-pink-500/40 blur-xl -z-10" />

              {/* Floating sparkles around the pill — staggered animation
                  so they twinkle on/off independently. Decorative only. */}
              <span aria-hidden className="absolute -top-3 left-10 text-lg shimmer-sparkle text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.95)]" style={{ animationDelay: "0s" }}>✦</span>
              <span aria-hidden className="absolute -top-4 right-16 text-base shimmer-sparkle text-white drop-shadow-[0_0_7px_rgba(255,255,255,0.9)]" style={{ animationDelay: "0.6s" }}>✧</span>
              <span aria-hidden className="absolute -bottom-2 left-1/3 text-sm shimmer-sparkle text-white drop-shadow-[0_0_6px_rgba(255,255,255,0.9)]" style={{ animationDelay: "1.2s" }}>✦</span>
              <span aria-hidden className="absolute -bottom-3 right-10 text-lg shimmer-sparkle text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.95)]" style={{ animationDelay: "1.8s" }}>✧</span>

              {/* Pulsing dot */}
              <span className="relative z-[2] flex h-4 w-4">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-300" />
              </span>
              <span className="relative z-[2] text-base sm:text-xl font-bold uppercase tracking-[0.18em] text-white whitespace-nowrap" style={{ fontFamily: "'Outfit', sans-serif" }}>
                No insurance needed
              </span>
              <span className="relative z-[2] hidden sm:inline-block h-6 w-px bg-white/30" />
              <span className="relative z-[2] hidden sm:inline-flex items-center gap-2 text-sm sm:text-base font-medium text-purple-100 whitespace-nowrap">
                <span className="inline-flex items-center justify-center min-w-[2.25rem] h-7 px-2 rounded-full bg-white/20 text-white font-bold text-xs tracking-wide">
                  70,000+
                </span>
                <span>patients treated through Polomar Pharmacy</span>
              </span>
            </div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5 }} className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-purple-600">Our Services</span>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mt-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Treatments Tailored to You</h2>
          </motion.div>
          <div className="grid md:grid-cols-12 gap-6">
            {/* GLP-1 Card */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ delay: 0.1 }} className="md:col-span-7 bg-gradient-to-br from-purple-50 to-white rounded-2xl border border-purple-100 p-8 lg:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-purple-600 flex items-center justify-center"><Activity className="w-6 h-6 text-white" /></div>
                <span className="text-xs font-bold uppercase tracking-widest text-purple-600">GLP-1 Therapy</span>
              </div>
              <h3 className="text-2xl font-semibold text-slate-800 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Weight Management & Metabolic Health</h3>
              <p className="text-slate-600 leading-relaxed mb-6">Compounded GLP-1 receptor agonists like Semaglutide and Tirzepatide for sustainable weight management.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {["Semaglutide with B12", "Tirzepatide with B12", "Weekly injections", "Physician monitored"].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0" />{item}</div>
                ))}
              </div>
            </motion.div>
            {/* Peptide card — not offered by GLPFlo. Framed strictly as a
                future waitlist with no therapeutic claims, per LegitScript
                readiness review. */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ delay: 0.2 }} className="md:col-span-5 relative bg-gradient-to-br from-amber-50 to-white rounded-2xl border border-amber-100 p-8 lg:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-amber-500 flex items-center justify-center"><Heart className="w-6 h-6 text-white" /></div>
                <span className="text-xs font-bold uppercase tracking-widest text-amber-600">Peptide Therapy</span>
              </div>
              <h3 className="text-2xl font-semibold text-slate-800 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Not available yet</h3>
              <p className="text-slate-600 leading-relaxed mb-4">GLPFlo does not currently offer peptide therapy. Join the waitlist and we&apos;ll email you if it ever becomes part of our licensed catalog.</p>
              <p className="text-xs text-slate-500">No claims are made about peptide safety, efficacy, or availability.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== FACT OR MYTH CAROUSEL ===== */}
      {/* Conversion CTA above Fact or Myth — walker image extends down past
          the CTA and fades out at the bottom so it visually bleeds into the
          top of the Fact-or-Myth carousel section beneath. */}
      <section className="relative pt-14 pb-56 sm:pt-16 sm:pb-72 lg:pt-20 lg:pb-96 overflow-hidden -mb-40 sm:-mb-56 lg:-mb-72" data-testid="cta-above-factmyth-section">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/641f1dde-2f83-49f3-b447-cba4e26d4334/images/0d6127e33b60b52f4d118b2b369aca92c23526c25b7909f926509d08b2c47d43.jpeg')",
            backgroundSize: "cover",
            backgroundPosition: "center 30%",
            opacity: 0.7,
            WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 75%, rgba(0,0,0,0.6) 90%, rgba(0,0,0,0) 100%)",
            maskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 75%, rgba(0,0,0,0.6) 90%, rgba(0,0,0,0) 100%)",
            zIndex: 0,
          }}
          data-testid="cta-factmyth-bg-walker"
        />
        {/* Softer readability overlay — lets the walker photo come through
            crisply behind the CTA while keeping the button copy legible. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.15) 35%, rgba(255,255,255,0.0) 65%, rgba(255,255,255,0.0) 100%)",
            zIndex: 0,
          }}
        />
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionCTA testid="cta-above-factmyth" size="lg" className="mt-0" />
        </div>
      </section>

      <LazyOnVisible minHeight={480}><FactOrMythCarousel /></LazyOnVisible>

      {/* ===== Million Mile Movement — Step-a-Thon hero ticker ===== */}
      <section
        className="relative py-16 lg:py-20 overflow-hidden text-white"
        style={{
          background: "linear-gradient(135deg, #1B0B3A 0%, #2A0E55 45%, #4C1D95 100%)",
        }}
        data-testid="million-mile-movement-section"
      >
        {/* Full-bleed "starting-line" runner background. Dark purple overlay
            keeps the copy legible while letting the image set the tone. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/641f1dde-2f83-49f3-b447-cba4e26d4334/images/85665f999d3b08419f387391bc215edc7ae3b0280c423ec9d82e78e56a077989.jpeg')",
            backgroundSize: "cover",
            backgroundPosition: "center 30%",
            opacity: 0.32,
            zIndex: 0,
          }}
          data-testid="normalize-journey-bg-runner"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background: "linear-gradient(180deg, rgba(27,11,58,0.55) 0%, rgba(27,11,58,0.35) 45%, rgba(76,29,149,0.65) 100%)",
            zIndex: 0,
          }}
        />
        {/* Big rotated red "Coming Soon" stamp — matches the Peptide card
            stamp style. Only shown when admin has Step Counter toggled OFF.
            Positioned prominently across the section; content behind fades
            to opacity-60 so the promise still reads. */}
        {stepathonComingSoon && (
          <div className="absolute top-8 sm:top-12 right-4 sm:right-16 z-20 -rotate-12" data-testid="stepathon-coming-soon-stamp">
            <div
              className="bg-red-600 text-white font-bold text-base sm:text-xl uppercase tracking-wider px-6 sm:px-10 py-3 sm:py-4 rounded-lg border-2 border-red-700 shadow-2xl"
              style={{ fontFamily: "'Outfit', sans-serif", textShadow: "1px 1px 2px rgba(0,0,0,0.35)" }}
            >
              Coming Soon
            </div>
          </div>
        )}
        <div className={stepathonComingSoon ? "opacity-60 pointer-events-none select-none" : ""}>
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] bg-pink-500/30 rounded-full blur-3xl" />
          <div className="absolute -bottom-32 -right-32 w-[28rem] h-[28rem] bg-purple-500/30 rounded-full blur-3xl" />
        </div>

        {/* Section-wide shimmer sweep — mirrors the "Am I a good fit?" CTA */}
        <span
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.08) 50%, transparent 65%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 6s linear infinite",
          }}
        />

        {/* Corner sparkles floating around the banner */}
        <Sparkles aria-hidden="true" className="absolute top-8 left-8 w-5 h-5 text-pink-200/80 animate-sparkle-float-slow" />
        <Sparkles aria-hidden="true" className="absolute top-14 right-16 w-4 h-4 text-violet-200/80 animate-sparkle-spin" style={{ animationDelay: "0.6s" }} />
        <Sparkles aria-hidden="true" className="absolute bottom-10 left-1/4 w-4 h-4 text-fuchsia-200/70 animate-sparkle-float-fast" style={{ animationDelay: "1.2s" }} />
        <Sparkles aria-hidden="true" className="absolute bottom-16 right-10 w-5 h-5 text-purple-200/80 animate-sparkle-float-slow" style={{ animationDelay: "1.8s" }} />
        <Sparkles aria-hidden="true" className="absolute top-1/2 left-6 w-3 h-3 text-pink-100/70 animate-sparkle-spin" style={{ animationDelay: "2.4s" }} />

        {/* Ghost-walker footsteps trail — 10 individual L/R footprints
            press down in sequence along the diagonal from top-left →
            bottom-right, each fading in like a foot landing, holding
            for a beat, then slowly disappearing (~10s loop, 0.55s
            stagger). `pointer-events-none` on wrapper so no CTA clicks
            are intercepted. Visible on all viewports. */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true" data-testid="normalize-footsteps-trail">
          {(() => {
            // Walker heads from (8%, 10%) to (86%, 84%) — top-left to
            // bottom-right (SE). Direction unit vector ≈ (0.725, 0.688).
            // Perpendicular to LEFT of walking dir (walker's left hand,
            // NE side of screen): (+0.688, -0.725). Perpendicular to
            // RIGHT (SW side of screen): (-0.688, +0.725). Offset ≈ 3%
            // for a natural shoulder-width gait at the bigger foot size.
            const OFF = 3.5;
            const perpL = { x:  0.688 * OFF, y: -0.725 * OFF };
            const perpR = { x: -0.688 * OFF, y:  0.725 * OFF };
            const N = 10;
            const STAGGER = 0.55; // seconds between successive foot-strikes
            return Array.from({ length: N }, (_, i) => {
              const t = i / (N - 1);
              const cx = 8 + 78 * t;
              const cy = 10 + 74 * t;
              const isLeft = i % 2 === 0;
              const off = isLeft ? perpL : perpR;
              // Walking direction SE = 135° clockwise from north (default
              // toes-up). Add slight toe-in variation per side (~4°) so
              // the gait has organic asymmetry.
              const rot = isLeft ? 139 : 131;
              return (
                <SingleFoot
                  key={i}
                  side={isLeft ? "L" : "R"}
                  className="ghost-foot"
                  style={{
                    "--x": `${(cx + off.x).toFixed(2)}%`,
                    "--y": `${(cy + off.y).toFixed(2)}%`,
                    "--rot": `${rot}deg`,
                    "--delay": `${(i * STAGGER).toFixed(2)}s`,
                    "--size": "clamp(30px, 3.6vw, 52px)",
                  }}
                />
              );
            });
          })()}
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          {/* Promo eyebrow — advertise that step points are redeemable for gifts + coupons. */}
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-pink-300/40 px-4 py-1.5 mb-4" data-testid="stepathon-promo-pill">
            <Sparkles className="w-3.5 h-3.5 text-pink-200" aria-hidden="true" />
            <span className="text-[11px] sm:text-xs font-bold uppercase tracking-widest text-pink-100">
              Earn step points · redeem for gifts &amp; coupons
            </span>
          </div>
          <p className="text-xs sm:text-sm tracking-[0.5em] uppercase text-pink-300 font-bold">
            GLPFlo · Step-a-Thon
          </p>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            Normalize the journey,
            <br />
            <span className="bg-gradient-to-r from-purple-200 to-pink-300 bg-clip-text text-transparent">
              one step at a time.
            </span>
          </h2>
          <div className="mt-10">
            <LiveStepCounter
              url="/api/stepathon/global/counter"
              label="Million Mile Movement"
              sublabel="lifetime steps across every GLPFlo step-a-thon"
              variant="hero"
              refreshMs={45000}
            />
          </div>
          <div className="mt-10">
            <button
              onClick={() => navigate("/stepathon")}
              data-testid="million-mile-cta-btn"
              className="group relative inline-flex items-center gap-2 text-white font-bold px-8 py-4 rounded-full transition-all hover:scale-[1.03] shadow-xl shadow-pink-900/40 overflow-hidden focus:outline-none focus:ring-4 focus:ring-pink-300"
              style={{
                background: "linear-gradient(110deg, #db2777 0%, #c026d3 35%, #a21caf 65%, #db2777 100%)",
                backgroundSize: "200% 100%",
                animation: "pulse-glow 2.8s ease-in-out infinite, shimmer 3.5s linear infinite",
              }}
            >
              {/* shimmer overlay */}
              <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-full"
                style={{
                  background: "linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.32) 50%, transparent 70%)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer 3.5s linear infinite",
                }} />
              {/* sparkles around the button */}
              <Sparkles aria-hidden="true" className="absolute -top-2 -left-1.5 w-4 h-4 text-fuchsia-200 animate-sparkle-float-slow" />
              <Sparkles aria-hidden="true" className="absolute -bottom-2 right-6 w-3 h-3 text-violet-200 animate-sparkle-float-fast" />
              <Sparkles aria-hidden="true" className="absolute top-1.5 right-3 w-3.5 h-3.5 text-pink-100 animate-sparkle-spin" style={{ animationDelay: "0.4s" }} />
              <Sparkles aria-hidden="true" className="absolute bottom-1.5 left-4 w-2.5 h-2.5 text-pink-200 animate-sparkle-float-slow" style={{ animationDelay: "1.1s" }} />

              <Sparkles className="relative w-5 h-5 drop-shadow-md group-hover:rotate-12 transition-transform" />
              <span className="relative">See active challenges</span>
              <ArrowRight className="relative w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="mt-3 text-xs text-purple-200/80">
              Free for every GLPFlo member · no purchase required
            </p>
          </div>

          {/* Step-to-Points reward strip */}
          <div className="mt-14 grid sm:grid-cols-3 gap-4 text-left">
            <div className="rounded-2xl border border-pink-300/30 bg-white/5 backdrop-blur p-5">
              <div className="text-[10px] uppercase tracking-widest text-pink-300 font-bold">
                1 step = 1 point
              </div>
              <h3 className="mt-2 text-lg font-extrabold">Walk &rarr; earn</h3>
              <p className="mt-1 text-sm text-purple-100/80">
                Every step you log on any GLPFlo challenge becomes a redeemable point in your pocket.
              </p>
            </div>
            <div className="rounded-2xl border border-pink-300/30 bg-white/5 backdrop-blur p-5">
              <div className="text-[10px] uppercase tracking-widest text-pink-300 font-bold">
                Spend on real perks
              </div>
              <LandingRewardsPerkTile />
            </div>
            <div className="rounded-2xl border border-pink-300/30 bg-white/5 backdrop-blur p-5">
              <div className="text-[10px] uppercase tracking-widest text-pink-300 font-bold">
                Shareable badges
              </div>
              <h3 className="mt-2 text-lg font-extrabold">Every milestone, one tap to share</h3>
              <p className="mt-1 text-sm text-purple-100/80">
                Hit a tier → download a custom PNG badge → post it. Move your friends too.
              </p>
            </div>
          </div>
          <div className="mt-8">
            <button
              onClick={() => navigate("/stepathon/rewards")}
              data-testid="rewards-landing-cta-btn"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 text-white font-bold px-6 py-3 rounded-full border border-purple-200/30 transition"
            >
              Browse the rewards catalogue &rarr;
            </button>
          </div>
          <SectionCTA testid="cta-normalize" size="lg" />
        </div>
        </div>
      </section>
      {/* YouTube channel preview — moved above "Our Simple Process" so visitors
          watch the care-team explainers before scanning the 3-step flow. */}
      <LazyOnVisible minHeight={480}><LandingYouTubeCarousel /></LazyOnVisible>

      {/* Our Simple Process */}
      <section id="how-it-works" className="py-12 lg:py-16 bg-slate-50" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Our Simple Process</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Create Your Account", desc: "Sign up and complete your medical intake questionnaire from the comfort of home.", icon: CheckCircle, clickable: true, link: "/register" },
              { step: "02", title: "Provider Review", desc: "A licensed prescriber reviews your information and approves your treatment plan (typically within 24 hours).", icon: Clock, clickable: false },
              { step: "03", title: "Pharmacy Delivery", desc: "Your prescription is sent to our compounding pharmacy and shipped directly to your door.", icon: Truck, clickable: false },
            ].map((item) => (
              <motion.div key={item.step} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ delay: Number(item.step) * 0.05 }}
                className={`relative bg-white rounded-2xl border border-purple-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ${item.clickable ? "cursor-pointer" : ""}`}
                onClick={item.clickable ? () => navigate(item.link) : undefined}
                data-testid={`how-it-works-step-${item.step}`}>
                <span className="text-6xl font-bold text-purple-100 absolute top-4 right-6" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.step}</span>
                <div className="w-12 h-12 rounded-2xl bg-purple-100 flex items-center justify-center mb-5"><item.icon className="w-6 h-6 text-purple-600" /></div>
                <h3 className="text-xl font-semibold text-slate-800 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.desc}</p>
                {item.clickable && <p className="text-purple-600 text-sm font-medium mt-3 flex items-center gap-1">Sign up now <ArrowRight className="w-3.5 h-3.5" /></p>}
              </motion.div>
            ))}
          </div>
          <SectionCTA testid="how-it-works-cta" />
        </div>
      </section>

      {/* ===== Companion App — Free tier + Subscription unlocks ===== */}
      <section className="relative py-12 lg:py-16 bg-gradient-to-br from-purple-700 via-purple-800 to-indigo-900 overflow-hidden" data-testid="companion-app-section">
        {/* Big rotated red "Coming Soon" stamp — mirrors the stepathon
            treatment. Shown while admin has Companion App toggled OFF in
            System Ops → Wellness Features. When on, stamp + darken vanish. */}
        {companionComingSoon && (
          <div className="absolute top-8 sm:top-12 right-4 sm:right-16 z-20 -rotate-12" data-testid="companion-coming-soon-stamp">
            <div
              className="bg-red-600 text-white font-bold text-base sm:text-xl uppercase tracking-wider px-6 sm:px-10 py-3 sm:py-4 rounded-lg border-2 border-red-700 shadow-2xl"
              style={{ fontFamily: "'Outfit', sans-serif", textShadow: "1px 1px 2px rgba(0,0,0,0.35)" }}
            >
              Coming Soon
            </div>
          </div>
        )}
        <div className={companionComingSoon ? "opacity-60 pointer-events-none select-none" : ""}>
        {/* Decorative glow */}
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-indigo-500/30 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: copy + benefits */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-400 text-emerald-950 text-xs font-bold uppercase tracking-wider mb-5 shadow-lg" data-testid="companion-free-badge">
                <Gift className="w-3.5 h-3.5" /> Free to install · no subscription needed
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Meet your <span className="text-amber-300">Companion App</span>
              </h2>
              <p className="text-purple-100 text-lg leading-relaxed mb-8 max-w-xl">
                Your pocket GLP-1 hub. Auto-track steps, redeem rewards, schedule shot reminders, and log body measurements — all free. Subscribers unlock food logging, macros & AI coaching.
              </p>

              {/* FREE features block */}
              <div className="mb-6" data-testid="companion-free-features">
                <p className="text-xs uppercase tracking-widest text-emerald-300 font-bold mb-3 inline-flex items-center gap-1.5">
                  <Gift className="w-3.5 h-3.5" /> Free · everyone gets these
                </p>
                <ul className="space-y-3">
                  {[
                    { icon: Footprints, label: "Auto step counter + Step-a-Thon rewards", detail: "1 step = 1 point · redeem for coupons + refill credits" },
                    { icon: Bell, label: "Weekly shot reminders", detail: "Email or SMS at your chosen day + time" },
                    { icon: Scale, label: "Body measurements + weight tracking", detail: "Tape measurements + side-effect chips" },
                    { icon: Sparkles, label: "Daily rewards nudge", detail: "'You're 1,247 steps from your next reward'" },
                  ].map((b) => (
                    <li key={b.label} className="flex items-start gap-3" data-testid={`companion-free-benefit-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "")}`}>
                      <span className="flex-shrink-0 w-9 h-9 rounded-xl bg-emerald-500/25 border border-emerald-300/40 flex items-center justify-center">
                        <b.icon className="w-4 h-4 text-emerald-200" />
                      </span>
                      <span>
                        <span className="block text-white font-semibold leading-tight">{b.label}</span>
                        <span className="block text-purple-200 text-sm mt-0.5">{b.detail}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* SUBSCRIPTION unlocks block */}
              <div className="mb-8" data-testid="companion-paid-features">
                <p className="text-xs uppercase tracking-widest text-amber-300 font-bold mb-3 inline-flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5" /> Subscribers also unlock
                </p>
                <ul className="space-y-3">
                  {[
                    { icon: Utensils, label: "Food logging + macro rings", detail: "USDA food database + barcode scanner" },
                    { icon: Dumbbell, label: "Exercise / Move logging", detail: "Cardio + strength — auto-calculated burn" },
                    { icon: BarChart3, label: "Fit-Quiz weight projection", detail: "Your actual weight overlaid on predicted curve" },
                    { icon: Bot, label: "AI wellness assistant", detail: "Not medical advice — clinician review always separate" },
                  ].map((b) => (
                    <li key={b.label} className="flex items-start gap-3" data-testid={`companion-paid-benefit-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "")}`}>
                      <span className="flex-shrink-0 w-9 h-9 rounded-xl bg-amber-400/25 border border-amber-300/40 flex items-center justify-center">
                        <b.icon className="w-4 h-4 text-amber-200" />
                      </span>
                      <span>
                        <span className="block text-white font-semibold leading-tight">{b.label}</span>
                        <span className="block text-purple-200 text-sm mt-0.5">{b.detail}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => {
                    // Route the visitor to the correct native store for their device.
                    // Android → Play Store listing for our published package.
                    // iOS → App Store search fallback (swap in a hard-coded App Store
                    //        listing URL once the iOS build ships).
                    // Anything else → the existing PWA install helper page.
                    const ua = navigator.userAgent || navigator.vendor || "";
                    const isAndroid = /android/i.test(ua);
                    const isIOS = /iPad|iPhone|iPod/i.test(ua) && !window.MSStream;
                    let target = "/companion/install";
                    if (isAndroid) {
                      target = "https://play.google.com/store/apps/details?id=com.emergent.pharmacyroutinghub.tjcnsu";
                    } else if (isIOS) {
                      target = "https://apps.apple.com/us/search?term=GLPFlo";
                    }
                    window.location.href = target;
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-400 hover:bg-emerald-300 text-emerald-950 px-8 py-4 text-base font-bold shadow-xl transition-colors"
                  data-testid="companion-cta-install-btn"
                >
                  <Smartphone className="w-5 h-5" /> Install free Companion App
                </button>
                <Button
                  className="rounded-full bg-amber-400 hover:bg-amber-300 text-amber-950 px-8 py-6 text-base font-bold shadow-xl"
                  onClick={() => { trackSignupClick("companion-start-subscription"); navigate("/register"); }}
                  data-testid="companion-cta-start-btn"
                >
                  Start a subscription <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  className="rounded-full bg-transparent text-white border-white/40 hover:bg-white/10 hover:text-white px-8 py-6 text-base font-semibold"
                  onClick={() => navigate("/login")}
                  data-testid="companion-cta-login-btn"
                >
                  Already a member? Open the app
                </Button>
              </div>
              <p className="text-xs text-purple-300 mt-4" data-testid="companion-fineprint">
                Rewards, shot reminders, and body tracking work without any subscription. Food logging + AI coaching require an active plan.
              </p>
            </motion.div>

            {/* Right: phone mockup */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="relative flex justify-center lg:justify-end">
              <div className="relative w-[280px] sm:w-[320px]">
                {/* Phone frame */}
                <div className="relative aspect-[9/19] bg-slate-900 rounded-[3rem] border-[10px] border-slate-800 shadow-2xl overflow-hidden">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-slate-800 rounded-b-2xl z-20" />
                  {/* Screen content */}
                  <div className="relative h-full bg-gradient-to-b from-purple-50 via-white to-slate-50 px-5 pt-10 pb-20 overflow-hidden">
                    {/* Status bar */}
                    <div className="flex justify-between text-[10px] font-semibold text-slate-700 mb-4">
                      <span>9:41</span>
                      <span>GLPFlo</span>
                    </div>
                    {/* Net calorie hero card */}
                    <div className="rounded-2xl bg-gradient-to-br from-purple-600 to-indigo-700 p-4 text-white shadow-lg mb-3">
                      <div className="text-[10px] uppercase tracking-wider text-purple-200 mb-1">Net calories today</div>
                      <div className="text-3xl font-bold">−285</div>
                      <div className="text-[11px] text-purple-200 mt-1">1,420 in · 1,705 out</div>
                    </div>
                    {/* Macro rings */}
                    <div className="rounded-xl bg-white border border-slate-200 p-3 mb-3 shadow-sm">
                      <div className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Macros vs. goal</div>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { label: "Protein", pct: 78, color: "bg-purple-500" },
                          { label: "Carbs", pct: 52, color: "bg-amber-500" },
                          { label: "Fat", pct: 41, color: "bg-pink-500" },
                        ].map((m) => (
                          <div key={m.label} className="text-center">
                            <div className="relative w-12 h-12 mx-auto mb-1">
                              <div className="absolute inset-0 rounded-full border-[3px] border-slate-100" />
                              <div className={`absolute inset-0 rounded-full border-[3px] border-transparent ${m.color}`} style={{ clipPath: `polygon(50% 50%, 50% 0, ${50 + m.pct * 0.5}% 0, 100% 100%, 0 100%, 0 0, 50% 0)` }} />
                              <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-slate-700">{m.pct}%</div>
                            </div>
                            <div className="text-[9px] text-slate-500">{m.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Streak chip */}
                    <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-center gap-2">
                      <Flame className="w-5 h-5 text-emerald-600" />
                      <div className="flex-1">
                        <div className="text-[11px] font-bold text-emerald-900">4-week streak</div>
                        <div className="text-[10px] text-emerald-700">1 more for $10 reward</div>
                      </div>
                      <div className="text-amber-500 text-lg">🔥</div>
                    </div>
                    {/* Bottom nav stub */}
                    <div className="absolute bottom-0 left-0 right-0 h-14 bg-white border-t border-slate-200 flex justify-around items-center px-3">
                      {["Home", "Food", "Move", "Body"].map((t, i) => (
                        <div key={t} className={`text-[9px] font-semibold ${i === 0 ? "text-purple-600" : "text-slate-400"}`}>{t}</div>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Floating amber sticker */}
                <div className="absolute -top-4 -right-4 sm:-right-8 bg-amber-400 text-amber-950 text-xs font-extrabold px-4 py-2 rounded-full shadow-xl rotate-6 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> FREE
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        </div>
      </section>

      {/* Medications Preview */}
      <section id="medications" className="py-12 lg:py-16 bg-white" data-testid="medications-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <span className="text-xs font-bold uppercase tracking-widest text-purple-600">Medications</span>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mt-3 mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Physician-Prescribed, Pharmacy-Compounded
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                All medications are prescribed by licensed providers and compounded at our partner <PharmacyLink testId="landing-polomar-link" />. Physician-reviewed and pharmacy-compounded per your prescription.
              </p>
              <div className="space-y-4">
                {["Physician-prescribed GLP-1 receptor agonists", "Compounded with Vitamin B12", "Certified handling & data security", "Ongoing provider monitoring"].map((item) => (
                  <div key={item} className="flex items-center gap-3"><CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0" /><span className="text-slate-700">{item}</span></div>
                ))}
              </div>
              <Button className="rounded-full bg-purple-600 hover:bg-purple-700 text-white px-8 py-6 text-lg mt-8" onClick={() => navigate("/pricing")} data-testid="med-section-cta-btn">
                View Medications <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="hidden lg:block">
              <img src={WELLNESS_IMG} alt="Active healthy lifestyle" className="rounded-3xl shadow-xl w-full h-[440px] object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== WEIGHT LOSS CALCULATOR ===== */}
      <LazyOnVisible minHeight={560}><WeightLossCalculator navigate={navigate} /></LazyOnVisible>

      {/* Conversion CTA above the Common Questions section */}
      <section className="py-10 bg-white" data-testid="cta-above-faq-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionCTA testid="cta-above-faq" size="lg" className="mt-0" />
        </div>
      </section>

      {/* FAQ teaser — Common questions. Collapsed by default; visitor clicks
          the "Click here to expand" link to reveal the LandingFaqList. */}
      <section className="py-16 lg:py-20 bg-white" data-testid="landing-faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Link
              to="/faq"
              className="inline-block text-3xl md:text-5xl font-bold tracking-tight text-slate-900 hover:text-purple-700 transition-colors"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              data-testid="landing-faq-heading-link"
            >
              Common questions
            </Link>
            <p className="text-base text-slate-500 mt-3 max-w-xl mx-auto">
              Quick answers about ordering, pricing, safety, and shipping.
            </p>
            {!faqExpanded && (
              <button
                type="button"
                onClick={() => setFaqExpanded(true)}
                className="mt-4 inline-block text-sm font-semibold text-purple-600 hover:text-purple-700 underline underline-offset-4"
                data-testid="landing-faq-expand-link"
              >
                Click here to expand
              </button>
            )}
          </div>
          {faqExpanded && (
            <>
              <p className="text-center text-sm text-slate-500 mb-6" data-testid="landing-faq-tap-hint">
                Tap any question to read.
              </p>
              <LandingFaqList />
            </>
          )}
        </div>
      </section>

      {/* ===== GLP-1 PROGRESS GRAPH ===== */}
      <LazyOnVisible minHeight={560}><GLP1ProgressGraph /></LazyOnVisible>

      {/* Testimonials section removed — no patient testimonials on the
          public site until every entry is verified against a signed media
          release (LegitScript readiness). */}
      {/* COA trust strip — every batch ships with a third-party Certificate
          of Analysis. Placed right before the final CTA so visitors see the
          transparency promise before they convert. */}
      <section className="bg-gradient-to-r from-emerald-50 via-white to-emerald-50 border-y border-emerald-100" data-testid="landing-coa-strip">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-600 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Every vial backed by a Certificate of Analysis
              </h3>
              <p className="text-sm text-slate-600">Type your lot number to download the third-party lab report for your exact batch.</p>
            </div>
          </div>
          <Link
            to="/coa"
            className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 text-sm font-semibold shadow-sm whitespace-nowrap"
            data-testid="landing-coa-strip-cta"
          >
            Verify your vial <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="relative py-12 lg:py-16 bg-purple-700 overflow-hidden" data-testid="cta-section">
        {/* Right-side smiling-couple background image. Same fade-into-purple
            treatment as the Normalize the Journey section for visual
            consistency. Hidden below sm so mobile copy stays readable. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-full sm:w-[70%] lg:w-[60%] hidden sm:block"
          style={{
            backgroundImage: "url('https://customer-assets-eiarnc6j.emergentagent.net/job_641f1dde-2f83-49f3-b447-cba4e26d4334/artifacts/k1u7qaq1_ChatGPT%20Image%20Sep%206%2C%202026%2C%2006_11_33%20PM.webp')",
            backgroundSize: "cover",
            backgroundPosition: "center right",
            WebkitMaskImage: "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.35) 45%, rgba(0,0,0,0.75) 100%)",
            maskImage: "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.35) 45%, rgba(0,0,0,0.75) 100%)",
            opacity: 0.6,
            zIndex: 0,
          }}
          data-testid="cta-bg-couple"
        />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>Ready to Take Control of Your Health?</h2>
            <p className="text-purple-200 text-lg mb-10 max-w-2xl mx-auto">Join thousands who have transformed their lives with GLPFlo. Your provider consultation is included in the price — no hidden fees, ever.</p>
            <Button className="rounded-full bg-white text-purple-700 hover:bg-purple-50 px-10 py-6 text-lg font-semibold shadow-xl" onClick={() => { trackSignupClick("bottom-cta-get-started"); navigate("/register"); }} data-testid="cta-get-started-btn">
              Get Started Now <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center"><Pill className="w-4 h-4 text-white" /></div>
                <span className="font-bold text-lg text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>GLPFlo</span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">Your trusted telehealth platform for GLP-1 and peptide medications.</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Patients</h4>
              <div className="space-y-2">
                <Link to="/login" className="block text-sm text-slate-400 hover:text-purple-400 transition-colors" data-testid="landing-footer-signin">Sign in</Link>
                <Link to="/register" onClick={() => trackSignupClick("footer-get-started")} className="block text-sm text-slate-400 hover:text-purple-400 transition-colors" data-testid="landing-footer-getstarted">Get started</Link>
                <Link to="/pricing" className="block text-sm text-slate-400 hover:text-purple-400 transition-colors" data-testid="landing-footer-pricing">Pricing &amp; medications</Link>
                <Link to="/browse-medications" className="block text-sm text-slate-400 hover:text-purple-400 transition-colors" data-testid="landing-footer-browse-medications">Browse medications</Link>
                <Link to="/forgot-password" className="block text-sm text-slate-400 hover:text-purple-400 transition-colors" data-testid="landing-footer-forgot-password">Forgot password?</Link>
                <a href="https://www.youtube.com/channel/UC2NOTygbPH-3JCkElwzNEWw" target="_blank" rel="noopener noreferrer" className="block text-sm text-slate-400 hover:text-purple-400 transition-colors" data-testid="landing-footer-youtube">GLPFlo on YouTube</a>
                <Link to="/coa" className="block text-sm text-slate-400 hover:text-purple-400 transition-colors" data-testid="landing-footer-coa">Verify your vial (COA lookup)</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Company</h4>
              <div className="space-y-2">
                <Link to="/pharmacy-info" className="block text-sm text-slate-400 hover:text-purple-400 transition-colors">Pharmacy Info</Link>
                <Link to="/faq" className="block text-sm text-slate-400 hover:text-purple-400 transition-colors" data-testid="landing-footer-faq">FAQ</Link>
                <Link to="/stepathon" className="block text-sm text-slate-400 hover:text-purple-400 transition-colors" data-testid="landing-footer-stepathon">Step-a-Thon</Link>
                <a href="#services" className="block text-sm text-slate-400 hover:text-purple-400 transition-colors">Services</a>
                <Link
                  to="/pharmacy-affiliate"
                  className="block text-sm text-slate-400 hover:text-purple-400 transition-colors font-medium"
                  data-testid="landing-footer-pharmacy-affiliate"
                >
                  Want to be a pharmacy affiliate?
                </Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Legal</h4>
              <div className="space-y-2">
                <Link to="/privacy-policy" className="block text-sm text-slate-400 hover:text-purple-400 transition-colors">Privacy Policy</Link>
                <Link to="/terms-of-service" className="block text-sm text-slate-400 hover:text-purple-400 transition-colors">Terms of Service</Link>
                <Link to="/online-tracking" className="block text-sm text-slate-400 hover:text-purple-400 transition-colors" data-testid="landing-footer-online-tracking">Online tracking disclosure</Link>
                <button
                  type="button"
                  onClick={() => {
                    try { localStorage.removeItem("glpflo_cookie_consent_v1"); } catch { /* noop */ }
                    window.location.reload();
                  }}
                  className="block text-sm text-slate-400 hover:text-purple-400 transition-colors text-left"
                  data-testid="landing-footer-cookie-settings"
                >Cookie settings</button>
                <Link to="/shipping-study" className="block text-sm text-slate-400 hover:text-purple-400 transition-colors" data-testid="landing-footer-shipping-study">Shipping Stability Study</Link>
                <Link
                  to="/hipaa-compliance"
                  className="block text-sm text-slate-400 hover:text-purple-400 transition-colors"
                  data-testid="landing-footer-hipaa-compliance"
                >
                  HIPAA Compliance
                </Link>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="text-sm text-slate-500">&copy; {new Date().getFullYear()} GLPFlo LLC. All rights reserved.</p>
            <p
              className="text-[11px] leading-relaxed text-slate-500 max-w-md md:text-center"
              data-testid="landing-footer-fda-disclosure"
            >
              Compounded semaglutide/tirzepatide is prepared by a state-licensed 503A/503B pharmacy and is not FDA-approved.{" "}
              <Link
                to="/shipping-study"
                className="text-purple-300 hover:text-purple-200 underline"
                data-testid="landing-footer-fda-shipping-study-link"
              >
                See the shipping stability study
              </Link>
              .
            </p>
            <div className="flex items-center gap-3">
              <HIPAABadge variant="footer" data-testid="landing-footer-hipaa-badge" />
              <p className="text-xs text-slate-600">Secure &middot; Licensed Telehealth Platform</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
